import { css, keyframes } from '@emotion/react'
import styled from '@emotion/styled'
import React, { useState, useCallback } from 'react'
import Slider, { Settings, CustomArrowProps } from 'react-slick'

import { PreviousArrowIcon, NextArrowIcon } from '@emico/icons'
import { overrideOptional } from '@emico/overrides'
import { globalWindow } from '@emico/ssr-utils'
import { theme } from '@emico/styles'

import ButtonUnstyled from './ButtonUnstyled'

const Wrapper = styled.div`
  position: relative;
`

const StyledSlider = styled(Slider)`
  display: flex;
  box-sizing: border-box;
  user-select: none;
  -webkit-touch-callout: none;
  -khtml-user-select: none;
  -ms-touch-action: pan-y;
  touch-action: pan-y;
  -webkit-tap-highlight-color: transparent;

  &.slick-initialized .slick-slide {
    display: flex;
  }

  &.slick-loading .slick-slide,
  &.slick-loading .slick-track {
    visibility: hidden;
  }

  &.slick-vertical .slick-slide {
    display: block;
    height: auto;
    border: 1px solid transparent;
  }

  .slick-list {
    flex: 1;
    display: block;
    overflow: hidden;
    margin: 0;
    padding: 0;

    &:focus {
      outline: none;
    }

    &.dragging {
      cursor: pointer;
      cursor: hand;
    }
  }

  .slick-track,
  .slick-list {
    transform: translate3d(0, 0, 0);
  }

  .slick-track {
    flex: 1;
    height: 100%;
    top: 0;
    left: 0;
    display: block;
    margin-left: auto;
    margin-right: auto;

    &:before,
    &:after {
      display: table;
      content: '';
    }

    &:after {
      clear: both;
    }
  }

  .slick-slide {
    display: flex;
    height: 100%;
    float: left;
    min-height: 1px;

    img {
      display: block;
    }

    // fixes slick-slide added div issue
    // added div has less width than slick-slide itself, therefore breaking slide child(ren)
    > div {
      width: 100%;
    }

    &.slick-loading img {
      display: none;
    }

    &.dragging img {
      pointer-events: none;
    }

    &.slick-active {
      display: flex;
    }
  }

  .slick-arrow.slick-hidden {
    display: none;
  }

  .slick-dots {
    position: absolute;
    bottom: 20px;
    right: 20px;
    left: 20px;
    display: block;
    padding: 0;
    margin: 0;
    list-style: none;
    text-align: right;

    li {
      position: relative;
      display: inline-block;
      width: 20px;
      height: 20px;
      padding: 0;
      cursor: pointer;

      + li {
        margin: 0 0 0 5px;
      }

      &.slick-active button {
        opacity: 0.9;
      }
    }

    li button {
      font-size: 0;
      line-height: 0;
      display: block;
      width: 20px;
      height: 20px;
      padding: 5px;
      cursor: pointer;
      color: transparent;
      border: 0;
      outline: none;
      opacity: 0.5;
      background: ${theme.backgroundColor};
      transition: all 200ms ease-in-out;

      &:hover,
      &:focus {
        opacity: 0.9;
      }
    }
  }
`

const ProgressBarAnimation = keyframes`
  from {
    opacity: 0.25;
    transform: translateX(-100%);
  }
  to {
    opacity: 1;
    transform: translateX(0%);
  }
`

const ProgressBar = styled.div<{ duration: number }>`
  pointer-events: none;
  position: absolute;
  bottom: 0;
  width: 100%;
  height: 5px;
  overflow: hidden;

  &::before {
    content: '';
    display: block;
    width: 100%;
    height: 5px;
    background: ${theme.secondaryColor};
    animation: ${ProgressBarAnimation} ${(props) => props.duration}ms linear
      forwards;
  }
`

const NavigationButton = styled(ButtonUnstyled)`
  position: absolute;
  top: 50%;
  width: 40px;
  height: 40px;
  padding: 0;
  border: 0;
  z-index: 1;
  text-align: center;
  transform: translateY(-50%);
  color: ${theme.onBackgroundColor};
`

const StyledPrevButton = styled(NavigationButton)`
  left: 0;
`

const StyledNextButton = styled(NavigationButton)`
  right: 0;
`

const iconStyling = css`
  font-size: 36px;
`

// override PreviousArrowIcon if different general previous icon is preferred
// override StyledPreviousArrowIcon if different Carousel-specific previous icon is preferred
export const StyledPreviousArrowIcon = overrideOptional(
  styled(PreviousArrowIcon)`
    ${iconStyling};
  `,
)

// override NextArrowIcon if different general next icon is preferred
// override StyledNextArrowIcon if different Carousel-specific next icon is preferred
export const StyledNextArrowIcon = overrideOptional(styled(NextArrowIcon)`
  ${iconStyling};
`)

const PrevButton = ({ onClick }: ArrowProps) => (
  <StyledPrevButton
    analyticsContext="Carousel"
    analyticsName="prev"
    onClick={onClick}
  >
    <StyledPreviousArrowIcon />
  </StyledPrevButton>
)

const NextButton = ({ onClick }: ArrowProps) => (
  <StyledNextButton
    analyticsContext="Carousel"
    analyticsName="next"
    onClick={onClick}
  >
    <StyledNextArrowIcon />
  </StyledNextButton>
)

declare global {
  interface Window {
    isPercolate: boolean
  }
}

const getAutoplay = (autoplay: boolean | undefined): boolean => {
  // TODO: SSR fixes required here
  if (globalWindow?.isPercolate) {
    return false
  }
  return autoplay || false
}

interface OwnProps {
  progressBar?: boolean
  hasArrows?: boolean
  asNavFor?: React.ReactNode
  children: React.ReactNode
  settings?: unknown
}

type Props = OwnProps & Settings & React.RefAttributes<Slider>
type ArrowProps = Pick<OwnProps, 'hasArrows'> & CustomArrowProps

/**
 * @deprecated Slick has several issues resulting in poor performance, bad
 * user experience, bad developer experience and poor accessability
 * Use the Slider component instead, or use no carousel at all.
 */
const Carousel = React.forwardRef<Slider, Props>(
  (
    {
      children,
      autoplay,
      autoplaySpeed,
      beforeChange,
      afterChange,
      progressBar,
      hasArrows = true,
      prevArrow,
      nextArrow,
      settings,
      ...others
    }: Props,
    ref,
  ) => {
    const hasAutoplay = getAutoplay(autoplay)

    const [animateProgress, setAnimateProgress] = useState<boolean | undefined>(
      hasAutoplay,
    )

    const BeforeChangeCallback = useCallback(
      (current: number, next: number) => {
        // https://github.com/akiran/react-slick/issues/136
        setTimeout(() => {
          beforeChange?.(current, next)
          if (progressBar && hasAutoplay) {
            setAnimateProgress(false)
          }
        }, 0)

        setTimeout(() => {
          if (progressBar && hasAutoplay) {
            setAnimateProgress(true)
          }
        }, 10)
      },
      [beforeChange, progressBar, hasAutoplay],
    )

    return (
      <Wrapper>
        <StyledSlider
          ref={ref}
          prevArrow={hasArrows ? prevArrow ?? <PrevButton /> : <></>}
          nextArrow={hasArrows ? nextArrow ?? <NextButton /> : <></>}
          autoplay={hasAutoplay}
          autoplaySpeed={autoplaySpeed}
          beforeChange={BeforeChangeCallback}
          {...(settings as any)}
          {...others}
        >
          {children}
        </StyledSlider>

        {progressBar && animateProgress && (
          <ProgressBar
            data-testid="Carousel.ProgressBar"
            duration={autoplaySpeed ? autoplaySpeed - 10 : 0}
          />
        )}
      </Wrapper>
    )
  },
)

Carousel.displayName = `Carousel(${Slider.name})`

export default Carousel
